import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import { ListItem, Button, Collapse, Stack, Typography, Popover, Box, useTheme } from '@mui/material';
import ChevronDownIcon from '../../../asset/icons/ChevronDown';
import ChevronRightIcon from '../../../asset/icons/ChevronRight';

const NavItem = (props) => {
  const {
    active = false,
    children,
    depth,
    icon,
    info,
    open: openProp = false,
    path,
    title,
    onNavigate,
    collapsed,
    ...other
  } = props;
  const theme = useTheme();
  const location = useLocation();

  const [open, setOpen] = useState(openProp);
  const [anchorEl, setAnchorEl] = useState(null);

  const openPopover = Boolean(anchorEl);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const isActive = path && location.pathname.startsWith(path);
  let paddingLeft = active ? 24 : 16;
  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const showPopover = (
    <Popover
      id="mouse-over-popover"
      sx={{
        pointerEvents: 'none',
      }}
      open={openPopover}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      onClose={handlePopoverClose}
      disableRestoreFocus
    >
      <Typography sx={{ px: 2, py: 0.5 }}>{title}</Typography>
    </Popover>
  );

  if (children) {
    return (
      <Stack direction="row">
        <ListItem
          disableGutters
          sx={{ display: 'block', py: 0 }}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          {...other}
        >
          <Button
            endIcon={!open ? <ChevronRightIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
            onClick={handleToggle}
            startIcon={icon}
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'medium',
              justifyContent: 'flex-start',
              pl: `${paddingLeft}px`,
              pr: '8px',
              py: '12px',
              textAlign: 'left',
              textTransform: 'none',
              width: `${collapsed ? '0px' : '100%'}`,
              '& svg': {
                color: 'action.active',
              },
            }}
            variant="text"
            component={RouterLink}
            to={path}
          >
            <Box sx={{ flexGrow: 1 }}>{collapsed ? '' : title}</Box>
            {info}
          </Button>
          <Collapse in={open}>{children}</Collapse>
        </ListItem>
        {collapsed ? showPopover : null}
      </Stack>
    );
  }

  return (
    <Stack direction="row">
      <ListItem
        disableGutters
        sx={{
          display: 'flex',
          py: 0,
          px: 0,
          ml: 0,
          ...(isActive && {
            borderLeft: '6px solid',
            borderLeftColor: theme.palette.primary.main,
            ml: -1,
          }),
        }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        {...other}
      >
        <Button
          component={path && RouterLink}
          startIcon={icon}
          onClick={() => onNavigate(path)}
          sx={{
            borderRadius: '0 12px 12px 0',
            color: theme.palette.text.primary,
            fontWeight: '500',
            justifyContent: 'flex-start',
            textAlign: 'left',
            pl: `${active ? (paddingLeft - 8) : paddingLeft}px`,
            pr: '8px',
            py: '12px',
            textTransform: 'none',
            width: '100%',
            '& svg': {
              color: 'action.active',
            },
            ...(isActive && {
              background: theme.palette.background.accent,
              fontWeight: 700,
            }),
          }}
          variant="text"
          to={path}
        >
          <Box sx={{ flexGrow: 1 }}>{collapsed ? '' : title}</Box>
          {info}
        </Button>
      </ListItem>
      {collapsed ? showPopover : null}
    </Stack>
  );
};

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
  onNavigate: PropTypes.func,
  collapsed: PropTypes.bool,
};

export default NavItem;
